import {
	type UseMutationOptions,
	type UseMutationResult,
	useMutation,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface ApiErrorResponse {
	error?: string;
	code?: string;
	message?: string;
}

export interface ToastMessages {
	loading?: string;
	success?: string;
	error?: string;
}

interface ContextWithToast {
	toastId?: string | number;
}

export interface MutationWithToastOptions<TData, TError, TVariables, TContext>
	extends Omit<
		UseMutationOptions<TData, TError, TVariables, TContext>,
		"onMutate" | "onSuccess" | "onError"
	> {
	toastMessages?: ToastMessages;
	onMutate?: (
		variables: TVariables,
	) => Promise<TContext> | TContext | undefined;
	onSuccess?: (
		data: TData,
		variables: TVariables,
		context: TContext | undefined,
	) => Promise<unknown> | unknown;
	onError?: (
		error: TError,
		variables: TVariables,
		context: TContext | undefined,
	) => Promise<unknown> | unknown;
	showLoadingToast?: boolean;
	showSuccessToast?: boolean;
	showErrorToast?: boolean;
}

/**
 * Custom hook that wraps useMutation with automatic toast notifications
 * Provides loading, success, and error toasts with i18n support
 */
export function useMutationWithToast<
	TData = unknown,
	TError = Error,
	TVariables = void,
	TContext = unknown,
>(
	options: MutationWithToastOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
	const { t } = useTranslation();

	const {
		toastMessages = {},
		onMutate,
		onSuccess,
		onError,
		showLoadingToast = true,
		showSuccessToast = true,
		showErrorToast = true,
		...mutationOptions
	} = options;

	return useMutation({
		...mutationOptions,
		onMutate: async (variables) => {
			let toastId: string | number | undefined;

			// Show loading toast if enabled
			if (showLoadingToast && toastMessages.loading) {
				toastId = toast.loading(toastMessages.loading);
			}

			// Call original onMutate if provided
			const context = await onMutate?.(variables);

			// Return context with toastId for cleanup
			return { ...context, toastId } as TContext & ContextWithToast;
		},
		onSuccess: async (data, variables, context) => {
			// Dismiss loading toast if it exists
			const contextWithToast = context as ContextWithToast;
			if (contextWithToast?.toastId) {
				toast.dismiss(contextWithToast.toastId);
			}

			// Show success toast if enabled
			if (showSuccessToast && toastMessages.success) {
				toast.success(toastMessages.success);
			}

			// Call original onSuccess if provided
			await onSuccess?.(data, variables, context);
		},
		onError: async (error, variables, context) => {
			// Dismiss loading toast if it exists
			const contextWithToast = context as ContextWithToast;
			if (contextWithToast?.toastId) {
				toast.dismiss(contextWithToast.toastId);
			}

			// Show error toast if enabled
			if (showErrorToast) {
				const axiosError = error as AxiosError<ApiErrorResponse>;
				const errorData = axiosError.response?.data;

				if (
					axiosError.response?.status === 403 &&
					errorData?.code === "LIMIT_EXCEEDED"
				) {
					toast.error(t("common.planLimitExceeded"));
				} else {
					const errorMessage =
						toastMessages.error || t("common.errors.generic");
					toast.error(errorMessage);
				}
			}

			// Call original onError if provided
			await onError?.(error, variables, context);
		},
	});
}
